import { AnimatePresence, motion } from 'framer-motion';
import { Check, Copy } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { TestParamDto } from '@/Domain/api/Action/dto/Action.dto';
import { Response } from '@/Domain/components/Action/CreateActionModal/Steps/TestRequest/Response';
import { TestResponseField } from '@/Domain/components/Action/HttpRequestActionSheet/TestResponse/TestResponseField';
import {
  METADATA_PREFIX,
  detectSystemVariables,
} from '@/Domain/components/Action/HttpRequestActionSheet/TestResponse/systemVariables';
import { useBootstrapConfig } from '@/Domain/components/App/BootstrapProvider';
import { IconButton } from '@/Domain/components/Shared/Button/Button';
import { FormItem } from '@/Domain/components/Shared/Form/Form';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { PlayIcon } from '@/Domain/components/icons/Play.icon';
import { HttpMethodEnum } from '@/Domain/enum/HttpMethod.enum';
import { ParameterTypeEnum } from '@/Domain/enum/ParameterType.enum';
import { ActionHelper } from '@/Domain/helper/Action.helper';
import { HttpActionHelper } from '@/Domain/helper/HttpAction.helper';
import { HttpActionForm } from '@/Domain/hooks/Action/ApiAction/useHttpRequestActionFormV2.hook';
import { useTestRequest } from '@/Domain/hooks/Action/ApiAction/useTestRequest.hook';
import { useCopiedFeedback } from '@/Domain/hooks/App/useCopiedFeedback.hook';
import { copyToClipboard } from '@/Domain/utils/copyToClipboard';
import { SCENARIO_ID } from '@/context';

type Props = {
  form: UseFormReturn<HttpActionForm>;
  actionId?: number;
};

export type HttpTestResponseForm = {
  ai_action: Omit<HttpActionForm, 'body_items_json_enable' | 'body_items_json'> &
    Record<'body_json', any>;
  parameters: Record<string, any>;
  quickchat_builtin?: Record<string, string>;
  quickchat_metadata?: Record<string, any>;
};

const TEST_CONVERSATION_ID = 'a1b2c3d4-e5f6-7890-abcd-ef1234567890';

const getLocalStorageKey = (actionId: number): string => `__qc_test_response_data_${actionId}`;

const getDefaultBuiltinValue = (name: string, apiServerUrl: string): string => {
  if (name === 'scenario_id') return SCENARIO_ID;
  if (name === 'conversation_id') return TEST_CONVERSATION_ID;
  if (name === 'conversation_url') {
    return `https://${apiServerUrl}/i/${SCENARIO_ID}/conversation/${TEST_CONVERSATION_ID}`;
  }
  if (name === 'conversation_channel') return 'widget';
  if (name === 'language') return 'en';
  if (name === 'country') return 'US';
  return '';
};

type CopyableKeyProps = {
  muted?: boolean;
  value: string;
};

const CopyableKey = ({ muted = false, value }: CopyableKeyProps) => {
  const { t } = useTranslation('common');
  const { copied, markCopied } = useCopiedFeedback();
  const feedback = copied ? t('copied') : t('copy');

  return (
    <button
      type="button"
      data-testid={`test-response-copy-key-${value}`}
      aria-label={`${feedback}: ${value}`}
      title={`${t('copy')}: ${value}`}
      className={`inline-flex w-fit items-center gap-1.5 text-sm hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        muted ? 'text-muted-foreground' : 'text-foreground'
      }`}
      onClick={() => copyToClipboard(value, markCopied)}
    >
      <span>{value || '-'}</span>
      {copied ? (
        <Check className="size-3.5 text-success" aria-hidden="true" />
      ) : (
        <Copy className="size-3.5" aria-hidden="true" />
      )}
    </button>
  );
};

export const TestResponse = ({ form, actionId }: Props) => {
  const { t } = useTranslation('actions_page');
  const { apiServerUrl } = useBootstrapConfig();
  const parameters = useWatch({ control: form.control, name: 'parameters' });
  const url = useWatch({ control: form.control, name: 'url' });
  const headers = useWatch({ control: form.control, name: 'headers' });
  const bodyItems = useWatch({ control: form.control, name: 'body_items' });
  const queryItems = useWatch({ control: form.control, name: 'query_items' });
  const bodyItemsJsonEnable = useWatch({ control: form.control, name: 'body_items_json_enable' });
  const bodyItemsJson = useWatch({ control: form.control, name: 'body_items_json' });

  const [testParams, setTestParams] = useState<TestParamDto[]>([]);
  const [builtinValues, setBuiltinValues] = useState<Record<string, string>>({});
  const [metadataValues, setMetadataValues] = useState<Record<string, string>>({});
  const [savedResponse, setSavedResponse] = useState<{
    response?: any;
    http_code?: number | null;
    bash_curl_command?: string;
  } | null>(null);

  const testParamsRef = useRef(testParams);
  testParamsRef.current = testParams;
  const builtinValuesRef = useRef(builtinValues);
  builtinValuesRef.current = builtinValues;
  const metadataValuesRef = useRef(metadataValues);
  metadataValuesRef.current = metadataValues;

  const hasLoadedSavedDataRef = useRef(false);

  const { handleTestRequest, jsonResponse, isPending } = useTestRequest();

  // Restore saved test data from localStorage on mount / actionId change
  useEffect(() => {
    hasLoadedSavedDataRef.current = false;
    if (!actionId) return;

    try {
      const raw = localStorage.getItem(getLocalStorageKey(actionId));
      if (!raw) return;
      const saved = JSON.parse(raw);
      hasLoadedSavedDataRef.current = true;

      if (saved.testParams) {
        setTestParams(saved.testParams as TestParamDto[]);
      }
      if (saved.builtinValues) {
        setBuiltinValues(saved.builtinValues);
      }
      if (saved.metadataValues) {
        setMetadataValues(saved.metadataValues);
      }
      if (saved.response !== undefined) {
        setSavedResponse({
          response: saved.response,
          http_code: saved.http_code,
          bash_curl_command: saved.bash_curl_command,
        });
      }
    } catch {
      // Ignore malformed localStorage data
    }
  }, [actionId]);

  // Persist to localStorage after a test run completes
  useEffect(() => {
    if (!jsonResponse || !actionId) return;

    const dataToSave = {
      testParams: testParamsRef.current,
      builtinValues: builtinValuesRef.current,
      metadataValues: metadataValuesRef.current,
      response: jsonResponse.response,
      http_code: jsonResponse.http_code,
      bash_curl_command: jsonResponse.bash_curl_command,
    };

    try {
      localStorage.setItem(getLocalStorageKey(actionId), JSON.stringify(dataToSave));
    } catch {
      // Ignore quota errors
    }
  }, [jsonResponse, actionId]);

  const displayResponse = jsonResponse || savedResponse;

  useEffect(() => {
    // Skip the sync when form parameters are still empty (before form.reset)
    // to avoid overwriting values just restored from localStorage.
    if (hasLoadedSavedDataRef.current && !parameters?.length) return;
    hasLoadedSavedDataRef.current = false;

    setTestParams((prevTestParams) =>
      (parameters ?? [])
        .filter((param) => !!param.name)
        .map((param) => {
          const normalizedName = `{{${param.name.replace(/\s+/g, '_')}}}`;
          const paramType = param.type as ParameterTypeEnum;

          const prevById = prevTestParams.find((testParam) => testParam.id === param.id);

          if (prevById) {
            const sameName = prevById.name === normalizedName;
            const sameType = prevById.type === paramType;

            return {
              id: param.id,
              name: normalizedName,
              type: paramType,
              value: sameName && sameType ? prevById.value : param.default_value,
            };
          }

          const prevSameNameAndType = prevTestParams.find(
            (testParam) => testParam.name === normalizedName && testParam.type === paramType,
          );

          return {
            id: param.id,
            name: normalizedName,
            type: paramType,
            value: prevSameNameAndType ? prevSameNameAndType.value : param.default_value,
          };
        }),
    );
  }, [parameters]);

  const detected = useMemo(
    () =>
      detectSystemVariables({
        url: url || '',
        headers: headers ?? [],
        body_items: bodyItems ?? [],
        query_items: queryItems ?? [],
        body_items_json_enable: bodyItemsJsonEnable ?? false,
        body_items_json: bodyItemsJson,
        parameters: parameters ?? [],
      } as HttpActionForm),
    [url, headers, bodyItems, queryItems, bodyItemsJsonEnable, bodyItemsJson, parameters],
  );

  useEffect(() => {
    setBuiltinValues((prev) => {
      const next: Record<string, string> = {};
      for (const name of detected.builtin) {
        next[name] = prev[name] ?? getDefaultBuiltinValue(name, apiServerUrl);
      }
      return next;
    });
  }, [detected.builtin, apiServerUrl]);

  useEffect(() => {
    setMetadataValues((prev) => {
      const next: Record<string, string> = {};
      for (const name of detected.metadata) {
        next[name] = prev[name] ?? '';
      }
      return next;
    });
  }, [detected.metadata]);

  const hasSystemVariables = detected.builtin.length > 0 || detected.metadata.length > 0;

  const submitTestResponse = () => {
    const { body_items_json_enable, body_items_json, ...formValues } = form.getValues();

    // Parsed up front: inline below, a bad body threw a SyntaxError out of the click handler.
    let parsedBodyJson: unknown = null;
    if (body_items_json_enable && body_items_json !== '{}') {
      try {
        parsedBodyJson = JSON.parse(
          HttpActionHelper.injectParameterIdsInString(
            body_items_json || '',
            formValues.parameters,
          ) || JSON.stringify({}, null, 2),
        );
      } catch {
        toast.error(t('sheet.form.validation.invalid_json'));
        return;
      }
    }

    const processedHeaders = formValues.headers.length
      ? HttpActionHelper.injectParameterIds(formValues.headers, formValues.parameters)
      : [];

    const body = body_items_json_enable
      ? []
      : formValues?.body_items.length
        ? HttpActionHelper.injectParameterIds(formValues.body_items, formValues.parameters)
        : [];

    const query = formValues?.query_items.length
      ? HttpActionHelper.injectParameterIds(formValues.query_items, formValues.parameters)
      : [];

    const payload: HttpTestResponseForm = {
      ai_action: {
        ...formValues,
        headers: processedHeaders as HttpActionForm['headers'],
        query_items: query as HttpActionForm['query_items'],
        body_items: body as HttpActionForm['body_items'],
        body_json: parsedBodyJson,
        method: formValues.method || HttpMethodEnum.GET,
        url: HttpActionHelper.injectParameterIdsForUrl(formValues.url, formValues.parameters),
      },
      parameters: ActionHelper.convertToObject(
        HttpActionHelper.injectParameterIds(testParams, formValues.parameters),
      ),
    };

    if (Object.keys(builtinValues).length > 0) {
      payload.quickchat_builtin = builtinValues;
    }

    if (Object.keys(metadataValues).length > 0) {
      payload.quickchat_metadata = metadataValues;
    }

    handleTestRequest(payload);
  };

  return (
    <TooltipProvider>
      <div className="space-y-7">
        <div>
          <section className="grid h-16 grid-cols-[1fr_1fr_auto_34px] items-center border-b border-border bg-accent px-4 text-sm font-medium text-muted-foreground">
            <span>
              {(testParams.length > 0 || hasSystemVariables) &&
                t('sheet.form.test_response.table.key')}
            </span>
            <span>
              {(testParams.length > 0 || hasSystemVariables) &&
                t('sheet.form.test_response.table.value')}
            </span>
            <span />
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton
                  size="sm"
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();

                    return submitTestResponse();
                  }}
                >
                  {isPending ? <Spinner className="size-6" /> : <PlayIcon />}
                </IconButton>
              </TooltipTrigger>
              <TooltipContent>{t('tooltip.test_the_api')}</TooltipContent>
            </Tooltip>
          </section>

          {testParams.length > 0 && (
            <div className="w-full">
              {testParams.map((param, index) => (
                <div key={param.name}>
                  <div className="grid grid-cols-[1fr_1fr_auto_34px] items-center h-16 px-4">
                    <FormItem>
                      <CopyableKey value={param.name.replaceAll('{{', '').replaceAll('}}', '')} />
                    </FormItem>
                    <FormItem>
                      <TestResponseField
                        name={param.name}
                        type={param.type}
                        value={param.value as string}
                        onChange={(value) =>
                          setTestParams((prevTestParams) =>
                            prevTestParams.map((testParam, i) =>
                              i === index ? { ...testParam, value: value } : testParam,
                            ),
                          )
                        }
                      />
                    </FormItem>
                  </div>

                  <Separator />
                </div>
              ))}
            </div>
          )}

          {hasSystemVariables && (
            <>
              <section className="grid h-10 grid-cols-[1fr_1fr_auto_34px] items-center border-y border-border bg-accent px-4 text-xs font-medium text-foreground-subtle">
                <span>{t('sheet.form.test_response.system_variables')}</span>
                <span />
                <span />
                <span />
              </section>

              {detected.builtin.map((name) => (
                <div key={`builtin-${name}`}>
                  <div className="grid grid-cols-[1fr_1fr_auto_34px] items-center h-16 px-4">
                    <FormItem>
                      <CopyableKey value={name} muted />
                    </FormItem>
                    <FormItem>
                      <Input
                        size="sm"
                        placeholder={name}
                        type="text"
                        value={builtinValues[name] ?? ''}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.stopPropagation();
                            event.preventDefault();
                          }
                        }}
                        onChange={(event) =>
                          setBuiltinValues((prev) => ({ ...prev, [name]: event.target.value }))
                        }
                      />
                    </FormItem>
                  </div>
                  <Separator />
                </div>
              ))}

              {detected.metadata.map((name) => (
                <div key={`metadata-${name}`}>
                  <div className="grid grid-cols-[1fr_1fr_auto_34px] items-center h-16 px-4">
                    <FormItem>
                      <CopyableKey value={name} muted />
                    </FormItem>
                    <FormItem>
                      <Input
                        size="sm"
                        placeholder={name.replace(METADATA_PREFIX, '')}
                        type="text"
                        value={metadataValues[name] ?? ''}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.stopPropagation();
                            event.preventDefault();
                          }
                        }}
                        onChange={(event) =>
                          setMetadataValues((prev) => ({ ...prev, [name]: event.target.value }))
                        }
                      />
                    </FormItem>
                  </div>
                  <Separator />
                </div>
              ))}
            </>
          )}
        </div>

        <AnimatePresence mode="wait">
          {!isPending && displayResponse && (
            <motion.div
              key="response"
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
            >
              <Response
                data={displayResponse?.response}
                httpCode={displayResponse?.http_code ?? undefined}
                bashCurlCommand={displayResponse?.bash_curl_command}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </TooltipProvider>
  );
};
